import { verifyAsync } from '@noble/ed25519';
/**
 * The signed sale QR of the Fuel PDA offline mode (docs/rebuild/fuel-pda.md
 * section 13.2): `MF1.<payload>.<signature>`, where `payload` is the base64url
 * (no padding) of the UTF-8 compact JSON below and `signature` the base64url
 * Ed25519 signature over the ASCII bytes `"MF1." + payload`.
 *
 * Encoding and verification run in Node and in the browser (the PDA verifies
 * offline), so this module uses no Node or DOM API. The server signs with Node
 * crypto (apps/api); `verifyMf1` uses @noble/ed25519.
 */
export const MF1_PREFIX = 'MF1.';
// --- byte helpers (no TextEncoder/btoa: they are not in every target lib) ---
const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const B64URL_INDEX = new Map([...B64URL].map((char, index) => [char, index]));
export function utf8Encode(text) {
    const bytes = [];
    for (const char of text) {
        const code = char.codePointAt(0);
        if (code < 0x80)
            bytes.push(code);
        else if (code < 0x800)
            bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        else if (code < 0x10000) {
            bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        }
        else {
            bytes.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        }
    }
    return Uint8Array.from(bytes);
}
/** Strict UTF-8 decoding; null on an invalid sequence. */
export function utf8Decode(bytes) {
    let out = '';
    let i = 0;
    while (i < bytes.length) {
        const b0 = bytes[i];
        let code;
        let extra;
        if (b0 < 0x80) {
            code = b0;
            extra = 0;
        }
        else if (b0 >= 0xc2 && b0 < 0xe0) {
            code = b0 & 0x1f;
            extra = 1;
        }
        else if (b0 >= 0xe0 && b0 < 0xf0) {
            code = b0 & 0x0f;
            extra = 2;
        }
        else if (b0 >= 0xf0 && b0 < 0xf5) {
            code = b0 & 0x07;
            extra = 3;
        }
        else
            return null;
        for (let j = 1; j <= extra; j += 1) {
            const next = bytes[i + j];
            if (next === undefined || (next & 0xc0) !== 0x80)
                return null;
            code = (code << 6) | (next & 0x3f);
        }
        if ((extra === 2 && (code < 0x800 || (code >= 0xd800 && code <= 0xdfff))) ||
            (extra === 3 && (code < 0x10000 || code > 0x10ffff))) {
            return null;
        }
        out += String.fromCodePoint(code);
        i += extra + 1;
    }
    return out;
}
export function base64UrlEncode(bytes) {
    let out = '';
    for (let i = 0; i < bytes.length; i += 3) {
        const b0 = bytes[i];
        const b1 = bytes[i + 1];
        const b2 = bytes[i + 2];
        out += B64URL[b0 >> 2];
        out += B64URL[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)];
        if (b1 !== undefined)
            out += B64URL[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)];
        if (b2 !== undefined)
            out += B64URL[b2 & 0x3f];
    }
    return out;
}
/** Decodes unpadded base64url; null on any other character or an impossible length. */
export function base64UrlDecode(text) {
    if (text.length % 4 === 1)
        return null;
    const bytes = [];
    let buffer = 0;
    let bits = 0;
    for (const char of text) {
        const value = B64URL_INDEX.get(char);
        if (value === undefined)
            return null;
        buffer = (buffer << 6) | value;
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            bytes.push((buffer >> bits) & 0xff);
        }
    }
    return Uint8Array.from(bytes);
}
// --- MF1 ---------------------------------------------------------------------
/**
 * The payload segment: base64url of the compact JSON with the keys in a fixed
 * order and absent optionals omitted.
 */
export function encodeMf1Payload(payload) {
    const ordered = {
        i: payload.i,
        r: payload.r,
        b: payload.b,
        p: payload.p,
        k: payload.k,
        q: payload.q,
        d: payload.d,
        n: payload.n,
    };
    if (payload.c !== undefined && payload.c !== '')
        ordered['c'] = payload.c;
    if (payload.v !== undefined && payload.v !== '')
        ordered['v'] = payload.v;
    ordered['t'] = payload.t;
    return base64UrlEncode(utf8Encode(JSON.stringify(ordered)));
}
/** The bytes the signature covers: ASCII `"MF1." + payloadSegment`. */
export function mf1SigningInput(payloadSegment) {
    return utf8Encode(MF1_PREFIX + payloadSegment);
}
export function assembleMf1(payloadSegment, signature) {
    return `${MF1_PREFIX}${payloadSegment}.${base64UrlEncode(signature)}`;
}
export function isMf1(text) {
    return text.trim().startsWith(MF1_PREFIX);
}
function isPayload(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    const text = (key) => typeof v[key] === 'string' && v[key] !== '';
    const optionalText = (key) => v[key] === undefined || typeof v[key] === 'string';
    return (text('i') &&
        text('r') &&
        text('b') &&
        (v['p'] === 'DIESEL' || v['p'] === 'PETROL') &&
        (v['k'] === 'CASH' || v['k'] === 'DEBT' || v['k'] === 'PDA') &&
        Array.isArray(v['q']) &&
        v['q'].every((line) => Array.isArray(line) &&
            line.length === 2 &&
            typeof line[0] === 'string' &&
            Number.isInteger(line[1]) &&
            line[1] > 0) &&
        typeof v['d'] === 'string' &&
        typeof v['n'] === 'string' &&
        optionalText('c') &&
        optionalText('v') &&
        Number.isInteger(v['t']) &&
        v['t'] >= 0);
}
/**
 * Splits and decodes an `MF1` string without checking the signature (the
 * server's lookups only need the reference); null when it is not well formed.
 */
export function parseMf1(raw) {
    const text = raw.trim();
    if (!text.startsWith(MF1_PREFIX))
        return null;
    const parts = text.slice(MF1_PREFIX.length).split('.');
    if (parts.length !== 2)
        return null;
    const [payloadSegment, signatureSegment] = parts;
    const payloadBytes = base64UrlDecode(payloadSegment);
    const signature = base64UrlDecode(signatureSegment);
    if (!payloadBytes || !signature || signature.length !== 64)
        return null;
    const json = utf8Decode(payloadBytes);
    if (json === null)
        return null;
    let payload;
    try {
        payload = JSON.parse(json);
    }
    catch {
        return null;
    }
    return isPayload(payload) ? { payload, payloadSegment, signature } : null;
}
/**
 * Verifies an `MF1` string with the downloaded public keys (section 13.6).
 * The key id must be known; an unknown key is invalid like a bad signature.
 */
export async function verifyMf1(raw, keys) {
    const parsed = parseMf1(raw);
    if (!parsed)
        return { ok: false, reason: 'MALFORMED' };
    const key = keys.find((candidate) => candidate.id === parsed.payload.i);
    const publicKey = key ? base64UrlDecode(key.publicKey) : null;
    if (!publicKey || publicKey.length !== 32)
        return { ok: false, reason: 'UNKNOWN_KEY' };
    let valid = false;
    try {
        valid = await verifyAsync(parsed.signature, mf1SigningInput(parsed.payloadSegment), publicKey, {
            zip215: false,
        });
    }
    catch {
        valid = false;
    }
    return valid ? { ok: true, payload: parsed.payload } : { ok: false, reason: 'BAD_SIGNATURE' };
}
/**
 * The reference inside scanned or typed text: the `r` of an `MF1` string
 * (unverified), otherwise the trimmed, upper-cased text. Used by every reader
 * that accepts a scanned receipt (operator lookup, dashboard search, PDA).
 */
export function scannedReferenceText(raw) {
    const parsed = parseMf1(raw);
    if (parsed)
        return parsed.payload.r.trim().toUpperCase();
    return raw.trim().toUpperCase();
}
//# sourceMappingURL=mf1.js.map